import { useState, useEffect, useCallback } from "react";
import { 
  CircleDot, Plus, Search, Loader2, Edit2, Trash2, Calendar,
  Package, Car, User, Phone, Clock, AlertTriangle, CheckCircle,
  ArrowRightLeft, Tag, MapPin, Thermometer
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription
} from "../components/ui/dialog";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Gommista({ user }) {
  const [activeTab, setActiveTab] = useState("deposito");
  const [depositi, setDepositi] = useState([]);
  const [cambiGomme, setCambiGomme] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDepositoDialog, setShowDepositoDialog] = useState(false);
  const [showCambioDialog, setShowCambioDialog] = useState(false);
  const [moduleEnabled, setModuleEnabled] = useState(false);
  
  const [depositoForm, setDepositoForm] = useState({
    customer_name: "",
    customer_phone: "",
    vehicle_plate: "",
    vehicle_brand: "",
    vehicle_model: "",
    tire_brand: "",
    tire_model: "",
    tire_size: "",
    tire_type: "invernali",
    quantity: "4",
    storage_location: "",
    condition: "buono",
    tread_depth_mm: "",
    notes: ""
  });

  const [cambioForm, setCambioForm] = useState({
    customer_name: "",
    customer_phone: "",
    vehicle_plate: "",
    vehicle_brand: "",
    vehicle_model: "",
    operation_type: "montaggio_deposito",
    tire_brand: "",
    tire_model: "",
    tire_size: "",
    tire_type: "invernali",
    quantity: "4",
    labor_hours: "1",
    notes: ""
  });

  const fetchModuleConfig = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/modules/my-config`, { });
      if (res.ok) {
        const data = await res.json();
        setModuleEnabled(data.gommista_enabled || false);
      }
    } catch (error) {
      console.error("Error fetching module config:", error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [depositiRes, cambiRes] = await Promise.all([
        authFetch(`${API}/modules/gommista/deposito${searchTerm ? `?search=${searchTerm}` : ""}`, { }),
        authFetch(`${API}/modules/gommista/cambi?limit=50`, { })
      ]);

      if (depositiRes.ok) setDepositi(await depositiRes.json());
      if (cambiRes.ok) setCambiGomme(await cambiRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchModuleConfig();
    fetchData();
  }, [fetchModuleConfig, fetchData]);

  const handleDepositoSubmit = async () => {
    try {
      const res = await authFetch(`${API}/modules/gommista/deposito`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...depositoForm,
          quantity: parseInt(depositoForm.quantity) || 4,
          tread_depth_mm: parseFloat(depositoForm.tread_depth_mm) || 0
        })
      });

      if (res.ok) {
        toast.success("Pneumatici depositati con successo");
        setShowDepositoDialog(false);
        resetDepositoForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Errore nel deposito");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleCambioSubmit = async () => {
    try {
      const res = await authFetch(`${API}/modules/gommista/cambio-diretto`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...cambioForm,
          quantity: parseInt(cambioForm.quantity) || 4,
          labor_hours: parseFloat(cambioForm.labor_hours) || 1
        })
      });

      if (res.ok) {
        toast.success("Cambio gomme registrato");
        setShowCambioDialog(false);
        resetCambioForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Errore nella registrazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleRitiro = async (depositoId) => {
    if (!window.confirm("Confermi il ritiro dei pneumatici?")) return;
    
    try {
      const res = await authFetch(`${API}/modules/gommista/deposito/${depositoId}/ritiro`, {
        method: "POST"
      });

      if (res.ok) {
        toast.success("Ritiro registrato");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  const resetDepositoForm = () => {
    setDepositoForm({
      customer_name: "",
      customer_phone: "",
      vehicle_plate: "",
      vehicle_brand: "",
      vehicle_model: "",
      tire_brand: "",
      tire_model: "",
      tire_size: "",
      tire_type: "invernali",
      quantity: "4",
      storage_location: "",
      condition: "buono",
      tread_depth_mm: "",
      notes: ""
    });
  };

  const resetCambioForm = () => {
    setCambioForm({
      customer_name: "",
      customer_phone: "",
      vehicle_plate: "",
      vehicle_brand: "",
      vehicle_model: "",
      operation_type: "montaggio_deposito",
      tire_brand: "",
      tire_model: "",
      tire_size: "",
      tire_type: "invernali",
      quantity: "4",
      labor_hours: "1",
      notes: ""
    });
  };

  // Stats
  const depositiAttivi = depositi.filter(d => d.status === "in_deposito").length;
  const depositiInvernali = depositi.filter(d => d.tire_type === "invernali" && d.status === "in_deposito").length;
  const depositiEstivi = depositi.filter(d => d.tire_type === "estivi" && d.status === "in_deposito").length;

  const tireTypeColors = {
    invernali: "bg-blue-500/20 text-blue-400",
    estivi: "bg-yellow-500/20 text-yellow-400",
    "4_stagioni": "bg-green-500/20 text-green-400"
  };

  const conditionColors = {
    ottimo: "text-green-400",
    buono: "text-emerald-400",
    discreto: "text-yellow-400",
    da_sostituire: "text-red-400"
  };

  if (!moduleEnabled) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="gommista-disabled">
        <div className="text-center">
          <CircleDot className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Modulo Gommista non abilitato</h2>
          <p className="text-zinc-400">Contatta l'amministratore per attivare questo modulo.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gommista-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white uppercase font-['Barlow_Condensed'] tracking-wider flex items-center gap-2">
            <CircleDot className="w-6 h-6 text-primary" />
            Gommista
          </h1>
          <p className="text-zinc-400 text-sm">Gestione deposito e cambio pneumatici stagionali</p>
        </div>
        
        <div className="flex gap-2">
          <Dialog open={showCambioDialog} onOpenChange={setShowCambioDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="btn-cambio">
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Cambio Gomme
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white uppercase font-['Barlow_Condensed']">Registra Cambio Gomme</DialogTitle>
                <DialogDescription className="text-zinc-400">Registra un'operazione di montaggio/smontaggio pneumatici</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400">Cliente *</label>
                    <Input
                      value={cambioForm.customer_name}
                      onChange={(e) => setCambioForm({...cambioForm, customer_name: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="Nome cliente"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Telefono</label>
                    <Input
                      value={cambioForm.customer_phone}
                      onChange={(e) => setCambioForm({...cambioForm, customer_phone: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="333..."
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400">Targa *</label>
                    <Input
                      value={cambioForm.vehicle_plate}
                      onChange={(e) => setCambioForm({...cambioForm, vehicle_plate: e.target.value.toUpperCase()})}
                      className="bg-zinc-800 border-zinc-700 uppercase"
                      placeholder="AA123BB"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Marca</label>
                    <Input
                      value={cambioForm.vehicle_brand}
                      onChange={(e) => setCambioForm({...cambioForm, vehicle_brand: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="FIAT"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Modello</label>
                    <Input
                      value={cambioForm.vehicle_model}
                      onChange={(e) => setCambioForm({...cambioForm, vehicle_model: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="500"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-zinc-400">Tipo Operazione</label>
                  <Select value={cambioForm.operation_type} onValueChange={(v) => setCambioForm({...cambioForm, operation_type: v})}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="montaggio_deposito">Montaggio da deposito</SelectItem>
                      <SelectItem value="smontaggio_deposito">Smontaggio per deposito</SelectItem>
                      <SelectItem value="montaggio_nuovo">Montaggio pneumatici nuovi</SelectItem>
                      <SelectItem value="cambio_completo">Cambio completo</SelectItem>
                      <SelectItem value="riparazione">Riparazione</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400">Marca Pneumatico</label>
                    <Input
                      value={cambioForm.tire_brand}
                      onChange={(e) => setCambioForm({...cambioForm, tire_brand: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="Michelin"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Misura</label>
                    <Input
                      value={cambioForm.tire_size}
                      onChange={(e) => setCambioForm({...cambioForm, tire_size: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="205/55 R16"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400">Tipo</label>
                    <Select value={cambioForm.tire_type} onValueChange={(v) => setCambioForm({...cambioForm, tire_type: v})}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="invernali">Invernali</SelectItem>
                        <SelectItem value="estivi">Estivi</SelectItem>
                        <SelectItem value="4_stagioni">4 Stagioni</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Quantità</label>
                    <Input
                      type="number"
                      value={cambioForm.quantity}
                      onChange={(e) => setCambioForm({...cambioForm, quantity: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      min="1"
                      max="8"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Ore Lavoro</label>
                    <Input
                      type="number"
                      step="0.5"
                      value={cambioForm.labor_hours}
                      onChange={(e) => setCambioForm({...cambioForm, labor_hours: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-zinc-400">Note</label>
                  <Input
                    value={cambioForm.notes}
                    onChange={(e) => setCambioForm({...cambioForm, notes: e.target.value})}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="Note aggiuntive..."
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowCambioDialog(false)}>Annulla</Button>
                <Button onClick={handleCambioSubmit} data-testid="btn-salva-cambio">Registra</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={showDepositoDialog} onOpenChange={setShowDepositoDialog}>
            <DialogTrigger asChild>
              <Button data-testid="btn-deposito">
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Deposito
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white uppercase font-['Barlow_Condensed']">Nuovo Deposito Pneumatici</DialogTitle>
                <DialogDescription className="text-zinc-400">Registra pneumatici per deposito stagionale</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400">Cliente *</label>
                    <Input
                      value={depositoForm.customer_name}
                      onChange={(e) => setDepositoForm({...depositoForm, customer_name: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="Nome cliente"
                      data-testid="input-deposito-cliente"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Telefono</label>
                    <Input
                      value={depositoForm.customer_phone}
                      onChange={(e) => setDepositoForm({...depositoForm, customer_phone: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="333..."
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400">Targa *</label>
                    <Input
                      value={depositoForm.vehicle_plate}
                      onChange={(e) => setDepositoForm({...depositoForm, vehicle_plate: e.target.value.toUpperCase()})}
                      className="bg-zinc-800 border-zinc-700 uppercase"
                      placeholder="AA123BB"
                      data-testid="input-deposito-targa"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Marca</label>
                    <Input
                      value={depositoForm.vehicle_brand}
                      onChange={(e) => setDepositoForm({...depositoForm, vehicle_brand: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="FIAT"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Modello</label>
                    <Input
                      value={depositoForm.vehicle_model}
                      onChange={(e) => setDepositoForm({...depositoForm, vehicle_model: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="500"
                    />
                  </div>
                </div>
                <div className="border-t border-zinc-800 pt-4">
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <CircleDot className="w-4 h-4 text-primary" />
                    Dati Pneumatici
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-zinc-400">Marca *</label>
                      <Input
                        value={depositoForm.tire_brand}
                        onChange={(e) => setDepositoForm({...depositoForm, tire_brand: e.target.value})}
                        className="bg-zinc-800 border-zinc-700"
                        placeholder="Michelin"
                        data-testid="input-deposito-tire-brand"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400">Modello</label>
                      <Input
                        value={depositoForm.tire_model}
                        onChange={(e) => setDepositoForm({...depositoForm, tire_model: e.target.value})}
                        className="bg-zinc-800 border-zinc-700"
                        placeholder="Pilot Sport"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div>
                      <label className="text-sm text-zinc-400">Misura *</label>
                      <Input
                        value={depositoForm.tire_size}
                        onChange={(e) => setDepositoForm({...depositoForm, tire_size: e.target.value})}
                        className="bg-zinc-800 border-zinc-700"
                        placeholder="205/55 R16"
                        data-testid="input-deposito-tire-size"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400">Tipo</label>
                      <Select value={depositoForm.tire_type} onValueChange={(v) => setDepositoForm({...depositoForm, tire_type: v})}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="invernali">Invernali</SelectItem>
                          <SelectItem value="estivi">Estivi</SelectItem>
                          <SelectItem value="4_stagioni">4 Stagioni</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400">Quantità</label>
                      <Input
                        type="number"
                        value={depositoForm.quantity}
                        onChange={(e) => setDepositoForm({...depositoForm, quantity: e.target.value})}
                        className="bg-zinc-800 border-zinc-700"
                        min="1"
                        max="8"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div>
                      <label className="text-sm text-zinc-400">Ubicazione</label>
                      <Input
                        value={depositoForm.storage_location}
                        onChange={(e) => setDepositoForm({...depositoForm, storage_location: e.target.value})}
                        className="bg-zinc-800 border-zinc-700"
                        placeholder="Scaffale A1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400">Stato</label>
                      <Select value={depositoForm.condition} onValueChange={(v) => setDepositoForm({...depositoForm, condition: v})}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ottimo">Ottimo</SelectItem>
                          <SelectItem value="buono">Buono</SelectItem>
                          <SelectItem value="discreto">Discreto</SelectItem>
                          <SelectItem value="da_sostituire">Da Sostituire</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400">Battistrada (mm)</label>
                      <Input
                        type="number"
                        step="0.1"
                        value={depositoForm.tread_depth_mm}
                        onChange={(e) => setDepositoForm({...depositoForm, tread_depth_mm: e.target.value})}
                        className="bg-zinc-800 border-zinc-700"
                        placeholder="5.5"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-zinc-400">Note</label>
                  <Input
                    value={depositoForm.notes}
                    onChange={(e) => setDepositoForm({...depositoForm, notes: e.target.value})}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="Note aggiuntive..."
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowDepositoDialog(false)}>Annulla</Button>
                <Button onClick={handleDepositoSubmit} data-testid="btn-salva-deposito">Deposita</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-primary" />
              <div>
                <p className="text-zinc-400 text-sm">Depositi Attivi</p>
                <p className="text-2xl font-bold text-white">{depositiAttivi}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Thermometer className="w-8 h-8 text-blue-400" />
              <div>
                <p className="text-zinc-400 text-sm">Invernali</p>
                <p className="text-2xl font-bold text-white">{depositiInvernali}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CircleDot className="w-8 h-8 text-yellow-400" />
              <div>
                <p className="text-zinc-400 text-sm">Estivi</p>
                <p className="text-2xl font-bold text-white">{depositiEstivi}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <ArrowRightLeft className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-zinc-400 text-sm">Cambi Registrati</p>
                <p className="text-2xl font-bold text-white">{cambiGomme.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900/50 border border-zinc-800">
          <TabsTrigger value="deposito" className="data-[state=active]:bg-primary" data-testid="tab-deposito">
            <Package className="w-4 h-4 mr-2" />
            Deposito ({depositiAttivi})
          </TabsTrigger>
          <TabsTrigger value="storico" className="data-[state=active]:bg-primary" data-testid="tab-storico">
            <Clock className="w-4 h-4 mr-2" />
            Storico Cambi
          </TabsTrigger>
        </TabsList>

        {/* Search */}
        <div className="flex gap-4 items-center mt-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              type="text"
              placeholder="Cerca per cliente, targa, marca..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-zinc-900/50 border-zinc-800"
              data-testid="input-search"
            />
          </div>
        </div>

        <TabsContent value="deposito" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : depositi.filter(d => d.status === "in_deposito").length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-500">Nessun pneumatico in deposito</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {depositi.filter(d => d.status === "in_deposito").map((deposito) => (
                <Card 
                  key={deposito.deposito_id} 
                  className="bg-zinc-900/50 border-zinc-800 hover:border-primary/50 transition-colors"
                  data-testid={`deposito-card-${deposito.deposito_id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg text-white flex items-center gap-2">
                        <Car className="w-4 h-4 text-primary" />
                        {deposito.vehicle_plate}
                      </CardTitle>
                      <Badge className={tireTypeColors[deposito.tire_type] || "bg-zinc-700"}>
                        {deposito.tire_type === "invernali" ? "Invernali" : 
                         deposito.tire_type === "estivi" ? "Estivi" : "4 Stagioni"}
                      </Badge>
                    </div>
                    <p className="text-zinc-400 text-sm">{deposito.vehicle_brand} {deposito.vehicle_model}</p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-zinc-500" />
                      <span className="text-white">{deposito.customer_name}</span>
                    </div>
                    {deposito.customer_phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-zinc-500" />
                        <span className="text-zinc-400">{deposito.customer_phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <CircleDot className="w-4 h-4 text-zinc-500" />
                      <span className="text-zinc-400">
                        {deposito.tire_brand} {deposito.tire_model} - {deposito.tire_size}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Tag className="w-4 h-4 text-zinc-500" />
                      <span className="text-zinc-400">Qtà: {deposito.quantity}</span>
                      {deposito.tread_depth_mm > 0 && (
                        <span className="text-zinc-500">| Battistrada: {deposito.tread_depth_mm}mm</span>
                      )}
                    </div>
                    {deposito.storage_location && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-zinc-500" />
                        <span className="text-zinc-400">{deposito.storage_location}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
                      <span className={`text-sm font-semibold ${conditionColors[deposito.condition] || "text-zinc-400"}`}>
                        {deposito.condition === "ottimo" ? "Ottimo" :
                         deposito.condition === "buono" ? "Buono" :
                         deposito.condition === "discreto" ? "Discreto" : "Da Sostituire"}
                      </span>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleRitiro(deposito.deposito_id)}
                        data-testid={`btn-ritiro-${deposito.deposito_id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Ritiro
                      </Button>
                    </div>
                    <p className="text-xs text-zinc-600">
                      Depositato: {new Date(deposito.created_at).toLocaleDateString("it-IT")}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="storico" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : cambiGomme.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-500">Nessun cambio gomme registrato</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Data</th>
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Cliente</th>
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Veicolo</th>
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Operazione</th>
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Pneumatici</th>
                    <th className="text-right py-3 px-4 text-zinc-400 font-medium">Ore</th>
                  </tr>
                </thead>
                <tbody>
                  {cambiGomme.map((cambio) => (
                    <tr key={cambio.cambio_id} className="border-b border-zinc-800/50 hover:bg-zinc-900/30">
                      <td className="py-3 px-4 text-zinc-400 text-sm">
                        {new Date(cambio.created_at).toLocaleDateString("it-IT")}
                      </td>
                      <td className="py-3 px-4 text-white">{cambio.customer_name}</td>
                      <td className="py-3 px-4">
                        <span className="text-primary font-mono">{cambio.vehicle_plate}</span>
                        <br />
                        <span className="text-zinc-400 text-sm">{cambio.vehicle_brand} {cambio.vehicle_model}</span>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className="bg-zinc-800">
                          {cambio.operation_type === "montaggio_deposito" ? "Montaggio da deposito" :
                           cambio.operation_type === "smontaggio_deposito" ? "Smontaggio per deposito" :
                           cambio.operation_type === "montaggio_nuovo" ? "Montaggio nuovi" :
                           cambio.operation_type === "cambio_completo" ? "Cambio completo" : "Riparazione"}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-zinc-400 text-sm">
                        {cambio.tire_brand} {cambio.tire_size}
                        <br />
                        <Badge className={`${tireTypeColors[cambio.tire_type]} text-xs`}>
                          {cambio.tire_type}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right text-white font-mono">{cambio.labor_hours}h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
